import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.swasthopd.swasth_opd";

const companyLinks = [
  { to: "/", label: "Home" },
  { to: "/plans", label: "Plans" },
  { to: "/contact", label: "Contact" },
];

const policyLinks = [
  { to: "/term-of-use", label: "Terms Of Use" },
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/cancellation-policy", label: "Cancellation Policy" },
  { to: "/shipping-delivery-policy", label: "Shipping and Delivery Policy" },
];

const socialLinks = [
  {
    href: "https://www.facebook.com/share/17MXed7usZ/",
    label: "Facebook",
    icon: <i className="fab fa-facebook-f" />,
  },
  {
    href: "https://x.com/AshwiniMeh25150",
    label: "X",
    icon: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M18.9 2H22l-6.8 7.8L23 22h-6.2l-4.9-6.4L6.3 22H3.2l7.3-8.3L1 2h6.3l4.4 5.9L18.9 2zm-1.1 18h1.7L6.3 3.9H4.5L17.8 20z" />
      </svg>
    ),
  },
  {
    href: "https://www.linkedin.com/company/swasthopd/",
    label: "LinkedIn",
    icon: <i className="fab fa-linkedin-in" />,
  },
];

const headingClass =
  "flex items-center gap-[8px] mb-[18px] text-[13px] font-bold uppercase tracking-[0.08em] text-white before:content-[''] before:w-[6px] before:h-[6px] before:rounded-full before:bg-[#d6ea4f]";
const linkClass =
  "text-[15px] font-medium leading-[1.4] text-white/70 no-underline transition-colors duration-200 hover:text-[#d6ea4f] focus:text-white/70 focus-visible:text-[#d6ea4f]";

const Footer = () => {
  const year = new Date().getFullYear();
  const [appStoreSoon, setAppStoreSoon] = useState(false);
  const soonTimer = useRef(null);

  // Tapping the App Store badge shows the "Coming soon" tag for a moment (touch has no hover)
  const showAppStoreSoon = () => {
    setAppStoreSoon(true);
    clearTimeout(soonTimer.current);
    soonTimer.current = setTimeout(() => setAppStoreSoon(false), 2200);
  };

  useEffect(() => () => clearTimeout(soonTimer.current), []);

  return (
    <footer className="relative overflow-hidden  text-white">
      <div className="max-w-[1180px] mx-auto px-[18px] min-[576px]:px-8 pt-[64px] min-[992px]:pt-[100px] pb-[18px] min-[576px]:pb-6">
        {/* Brand + tagline */}
        <div className="grid gap-8 min-[992px]:grid-cols-[minmax(0,340px)_minmax(0,1fr)] min-[992px]:gap-16 min-[992px]:items-start">
          <div>
            <Link to="/" className="inline-block">
              <img
                src="/assets/img/logo/swastha-opd-logo-1.png"
                alt="Swasth OPD"
                className="h-[64px] w-auto rounded-[10px]"
              />
            </Link>
            <p className="mt-[16px] mb-[4px] text-[13px] font-medium text-[#010c09]">
              © {year} Swasth OPD. All rights reserved.
            </p>
            <p className="my-[0px] text-[13px] font-medium text-[#010c09]">GST: 07AAZCA1903E1ZP</p>
          </div>

          <p className="my-[0px] text-[22px] min-[576px]:text-[26px] min-[1201px]:text-[30px] font-extrabold leading-[1.25] tracking-[-0.02em] text-[#0d2f27]">
            With Swasth OPD, you gain more than a health plan —{" "}
            <span className="relative inline-block text-[#1f8a4f]">
              you gain a trusted partner
              <svg
                className="absolute -left-[2%] w-[104%] -bottom-[8px] h-[10px] pointer-events-none"
                viewBox="0 0 220 14"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path d="M3 11 C 60 2, 150 1, 217 7" fill="none" stroke="#d6ea4f" strokeWidth="5" strokeLinecap="round" />
              </svg>
            </span>{" "}
            for your family’s everyday healthcare.
          </p>
        </div>

        {/* Panel */}
        <div className="relative bg-[#0b2a23] bg-hero-card bg-hero-grid mt-12 overflow-hidden rounded-[24px] border-[1px] border-solid border-white/[0.08] bg-white/[0.04] px-6 min-[576px]:px-10 min-[1201px]:px-16 pt-12">
          <div className="grid gap-y-10 gap-x-8 min-[576px]:grid-cols-2 min-[992px]:grid-cols-[1fr_1.3fr_1.4fr_1.3fr]">
            <div>
              <h4 className={headingClass}>Company</h4>
              <ul className="list-none pl-[0px] my-[0px] flex flex-col gap-[12px]">
                {companyLinks.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className={linkClass}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className={headingClass}>Policies</h4>
              <ul className="list-none pl-[0px] my-[0px] flex flex-col gap-[12px]">
                {policyLinks.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className={linkClass}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className={headingClass}>Reach us</h4>
              <ul className="list-none pl-[0px] my-[0px] flex flex-col gap-[14px]">
                <li className="flex gap-[10px] text-[15px] leading-[1.5] text-white/70">
                  <i className="fas fa-map-marker-alt mt-[4px] w-[14px] shrink-0 text-[#d6ea4f]" aria-hidden="true" />
                  642/1 Plot 30, Najafgarh Road Ind Area, West Delhi-110015
                </li>
                <li className="flex gap-[10px] items-center">
                  <i className="fas fa-phone-alt w-[14px] shrink-0 text-[#d6ea4f]" aria-hidden="true" />
                  <a href="tel:18003092740" className={linkClass}>
                    1800 309 2740
                  </a>
                </li>
                <li className="flex gap-[10px] items-center">
                  <i className="fas fa-envelope w-[14px] shrink-0 text-[#d6ea4f]" aria-hidden="true" />
                  <a href="mailto:hello@swasthopd.com" className={linkClass}>
                    hello@swasthopd.com
                  </a>
                </li>
              </ul>
            </div>

            <div>
              {/* <h4 className={headingClass}>Stay in touch</h4>
              <div className="flex gap-[10px]">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="flex items-center justify-center w-[38px] h-[38px] rounded-full border-[1px] border-solid border-white/[0.18] bg-white/[0.06] text-[14px] text-white no-underline transition duration-200 hover:bg-[#d6ea4f] hover:border-[#d6ea4f] hover:text-[#0d2f27] focus:text-white"
                  >
                    {social.icon}
                  </a>
                ))}
              </div> */}

              <h4 className={`${headingClass} mt-8`}>Get the app</h4>
              <div className="flex flex-wrap gap-[12px] pt-[4px]">
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get it on Google Play"
                  className="block rounded-[10px] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_-10px_rgba(0,0,0,0.6)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#d6ea4f]"
                >
                  <img src="/assets/img/play-store-badge.png" alt="" className="block h-[44px] w-auto" />
                </a>

                {/* No App Store listing yet: show a "Coming soon" tag on hover, focus or tap */}
                <div className="group relative">
                  <button
                    type="button"
                    aria-label="Download on the App Store — coming soon"
                    aria-describedby="app-store-soon"
                    onClick={showAppStoreSoon}
                    className="block p-[0px] bg-transparent border-[0px] rounded-[10px] cursor-pointer transition duration-200 hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#d6ea4f]"
                  >
                    <img src="/assets/img/app-store-badge.png" alt="" className="block h-[44px] w-auto" />
                  </button>
                  <span
                    id="app-store-soon"
                    role="status"
                    className={`pointer-events-none absolute left-1/2 bottom-[calc(100%+10px)] z-10 flex items-center gap-[6px] whitespace-nowrap rounded-full bg-[#d6ea4f] py-[4px] pl-[6px] pr-[12px] text-[12px] font-bold text-[#0d2f27] shadow-[0_8px_20px_-8px_rgba(0,0,0,0.5)] -translate-x-1/2 transition duration-200 after:content-[''] after:absolute after:left-1/2 after:top-full after:-translate-x-1/2 after:border-[6px] after:border-solid after:border-transparent after:border-t-[#d6ea4f] group-hover:opacity-[1] group-hover:translate-y-0 group-has-[:focus-visible]:opacity-[1] group-has-[:focus-visible]:translate-y-0 ${
                      appStoreSoon ? "opacity-[1] translate-y-0" : "opacity-[0] translate-y-1"
                    }`}
                  >
                    <DotLottieReact
                      src="/assets/lottie/coming-soon.json"
                      loop
                      autoplay
                      className="w-[20px] h-[20px] shrink-0"
                    />
                    Coming soon
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Wordmark */}
          <div
            aria-hidden="true"
            className="mt-14 -mb-[0.1em] select-none text-center px-[0.08em] text-[clamp(48px,13vw,168px)] font-extrabold leading-none tracking-[-0.04em] whitespace-nowrap text-transparent bg-clip-text bg-gradient-to-b from-white/[0.20] to-white/2"
          >
            Swasth OPD
          </div>
        </div>

        <p className="mt-[18px] mb-[0px] text-center text-[15px] font-bold text-[#1f8a4f]">
          Made for healthier families with{" "}
          <span className="text-red-600" aria-label="love">♥</span> by Swasth OPD
        </p>
      </div>
    </footer>
  );
};

export default Footer;
